'use client';

import React from 'react';
import { Box, IconButton, Tooltip, Chip } from '@mui/material';
import { AccessTimeIcon, PlaceIcon } from '@/shared/ui/display/EmojiIcon';

interface CounselMapSettingsProps {
  viewByTime: boolean;
  /** undefined면 시간 토글 버튼 숨김 (외부에서 viewByTime 고정 시) */
  onViewByTimeChange?: (checked: boolean) => void;
  /** 근무구역 표시명 (codePath 등), Chip 호버 시 툴팁으로 상세 표시 */
  regionLabels?: string[];
  /** overlay: 지도 위 absolute 배치, inline: flex 컨테이너 내 일반 배치 */
  placement?: 'overlay' | 'inline';
}

/**
 * CounselMap 컴팩트 설정 UI
 * - 시간으로 보기 토글 (IconButton + Tooltip)
 * - 근무구역 요약 (Chip, 호버 시 상세)
 */
export function CounselMapSettings({
  viewByTime,
  onViewByTimeChange,
  regionLabels = [],
  placement = 'overlay',
}: CounselMapSettingsProps) {
  const isOverlay = placement === 'overlay';
  return (
    <Box
      sx={{
        ...(isOverlay && {
          position: 'absolute',
          top: 6,
          right: 6,
          left: 6,
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' as const },
        }),
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        gap: 0.5,
      }}
    >
      {regionLabels.length > 0 && (
        <Tooltip
          title={
            <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem' }}>
              {regionLabels.map((label, i) => (
                <li key={i}>{label} 구역</li>
              ))}
            </Box>
          }
          enterTouchDelay={0}
          slotProps={{
            popper: {
              modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
            },
          }}
        >
          <Chip
            icon={<PlaceIcon sx={{ fontSize: 14 }} />}
            label={`구역 ${regionLabels.length}개`}
            size="small"
            variant="outlined"
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              fontSize: '0.7rem',
              height: 28,
              '& .MuiChip-icon': { color: 'text.secondary' },
            }}
          />
        </Tooltip>
      )}
      {onViewByTimeChange && (
        <Tooltip title="시간으로 보기">
          <IconButton
            size="small"
            onClick={() => onViewByTimeChange(!viewByTime)}
            color={viewByTime ? 'primary' : 'default'}
            aria-label="시간으로 보기"
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              p: 0.5,
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
